import {createContext, useContext, useState, ReactNode} from 'react';

interface ServiceCaseDetailActionsContextType {
  executeAction: <T>(key: string, action: () => Promise<T>) => Promise<T>;
  isActionPending: (key: string) => boolean;
  isAnyActionPending: boolean;
  getPendingActions: () => string[];
}

const ServiceCaseDetailActionsContext = createContext<
  ServiceCaseDetailActionsContextType | undefined
>(undefined);

export function ServiceCaseDetailActionsProvider(props: {children: ReactNode}) {
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());

  const executeAction = <T,>(key: string, action: () => Promise<T>): Promise<T> => {
    setPendingActions((prev) => new Set(prev).add(key));

    return action().finally(() => {
      setPendingActions((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    });
  };

  const isActionPending = (key: string): boolean => {
    return pendingActions.has(key);
  };

  const isAnyActionPending = pendingActions.size > 0;

  const getPendingActions = (): string[] => {
    return Array.from(pendingActions);
  };

  return (
    <ServiceCaseDetailActionsContext.Provider
      value={{
        executeAction,
        isActionPending,
        isAnyActionPending,
        getPendingActions,
      }}
    >
      {props.children}
    </ServiceCaseDetailActionsContext.Provider>
  );
}

export function useServiceCaseDetailActions() {
  const context = useContext(ServiceCaseDetailActionsContext);
  if (!context) {
    throw new Error(
      'useServiceCaseDetailActions must be used within ServiceCaseDetailActionsProvider'
    );
  }
  return context;
}
